import React from 'react';
import '../../App.css';
import './visualisation.css';
import '../../i18n/i18n';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// icons

// classes
import ToolSmall from '../../tools/tool-small';

// types
import { ComputeServiceType, ToolType } from '../../utils/types';

// enum

// functions

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import { setTab, closeTab } from './interactive-sessions-slice';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	H T M L   C U S T O M   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	------------------------------------------------------------
//
//	Displays the tool window for a given tool.
//
//	------------------------------------------------------------

function ToolWindow(args: {
  service: ComputeServiceType;
  toolComponent?: any;
  running: boolean;
}) {
  //
  if (args.toolComponent !== undefined) {
    //
    // a tool component has been specified, so this tool should be displayed in the tab.
    const Tool = args.toolComponent;
    return <Tool key={args.running === true ? 'T' : 'F'} running={args.running} />;
    //
  } else {
    //
    // no tool component has been specified, so we are displaying the url in an iFrame.
    return <iframe src={args.service.service_url} className="iframe" />;
    //
  }
  //
} // ToolWindow

//	--------------------------------------------------------------------------
//
//	C L A S S / M A I N   F U N C T I O N   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function InteractiveSessionsWindow() {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // currently selected tool.
  const sSelectedTool: ToolType = useAppSelector((state) => state.app.selectedTool);

  // tab information.
  const sTab: number[] = useAppSelector((state) => state.interactiveSessions.tab);
  const sTabs: {
    service: ComputeServiceType;
    toolComponent?: any;
  }[][] = useAppSelector((state) => state.interactiveSessions.tabs);

  //	-------------------------------------------------
  //
  //	C O N S T A N T S
  //
  //	-------------------------------------------------

  const toolType: ToolType[] = [
    ToolType.VISUALISE_DATA,
    ToolType.NOTEBOOK,
    ToolType.VIRTUAL_DESKTOPS,
  ];

  //	-------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	-------------------------------------------------

  // multi-language support
  const { i18n, t } = useTranslation();

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Check if a name begins with a particular prefix, and return
  //	the remainder of the name as a string and an integer.
  //
  //	------------------------------------------------------------

  function getPostfix(args: { name: string; prefix: string }) {
    //
    var postfix: { text: string; int: number } = { text: '', int: -1 };

    if (args.name.length > args.prefix.length) {
      if (args.name.slice(0, args.prefix.length) === args.prefix) {
        //
        // cut the rest of the text to get the postfix.
        postfix.text = args.name.slice(args.prefix.length - args.name.length);

        // convert to an integer.
        try {
          postfix.int = parseInt(postfix.text);
        } catch {}
        //
      }
    }

    // return something.
    return postfix;
    //
  } // getPostfix

  //	------------------------------------------------------------
  //
  //	Handler for button clicks.
  //
  //	------------------------------------------------------------

  const onClickButtonHandler = (
    event: React.MouseEvent<HTMLButtonElement>,
    args: { index: number },
  ) => {
    //
    const button: HTMLButtonElement = event.currentTarget;

    // changing tabs.
    var tabID: { text: string; int: number } = getPostfix({
      name: button.name,
      prefix: 'tab-',
    });
    if (tabID.int > -1) {
      dispatch(setTab({ tab: tabID.int, index: args.index }));
    }

    // closing tabs.
    tabID = getPostfix({
      name: button.name,
      prefix: 'close-session-',
    });
    if (tabID.int > -1) {
      dispatch(closeTab({ tab: tabID.int, index: args.index }));
    }
    //
  }; // onClickButtonHandler

  //	-------------------------------------------------
  //
  //	H O O K S
  //
  //	-------------------------------------------------

  //	-------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	-------------------------------------------------

  return (
    <>
      {
        //
        // loop over the three interactive session types.
        toolType.map((tool, toolTypeIndex) => (
          //
          <div
            className="panel-main"
            style={{ display: sSelectedTool === tool ? 'flex' : 'none' }}
          >
            {/*
        ///  we have a header row containing the tabs for running tools
        */}
            <div
              role="navigation"
              aria-label="Navigate web resources"
              className="visualisation-tab-bar"
              data-hide={sTabs[toolTypeIndex].length === 0 ? 'T' : 'F'}
            >
              <div style={{ flex: '0 0 10px' }} />
              {
                // display one tab for each tool that is currently running.
                sTabs[toolTypeIndex].map((item, index) => (
                  <ToolSmall
                    key={index.toString() + item.service.service_name}
                    name={'tab-' + index.toString()}
                    closeid={'close-session-' + index.toString()}
                    text={item.service.service_name}
                    withNumber={false}
                    withClose={true}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                      onClickButtonHandler(e, { index: toolTypeIndex })
                    }
                    selected={sTab[toolTypeIndex] === index}
                    left={index === 0}
                    right={index === sTabs[toolTypeIndex].length - 1}
                  />
                ))
              }
            </div>

            {
              // display the tool windows for each of the tools, making them visible only if the
              // currently selected tab matches this tool.
              sTabs[toolTypeIndex].map((item, index) => (
                <div
                  key={index}
                  className="visualisation-section"
                  data-visible={sTab[toolTypeIndex] === index ? 'T' : 'F'}
                >
                  <ToolWindow
                    service={item.service}
                    toolComponent={item.toolComponent}
                    running={true}
                  />
                </div>
              ))
            }
          </div>
          //
        ))
      }
    </>
  );
  //
} // InteractiveSessionsWindow
